import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShoppingBag, Check, BookOpen } from "lucide-react";
import { getBookBySlug, books } from "@/data/books";
import { getStoryBySlug } from "@/data/stories";
import BookCover from "@/components/BookCover";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";

export default function BookPage() {
  const { slug } = useParams();
  const book = getBookBySlug(slug);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!book) {
    return (
      <div className="min-h-[70vh] pt-40 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-display text-4xl font-semibold text-[#051937]">
          Tej książki jeszcze nie ma ✨
        </h1>
        <Link
          to="/sklep"
          className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#051937] text-white px-6 py-3 font-semibold"
        >
          Do sklepu
        </Link>
      </div>
    );
  }

  const currentIdx = books.findIndex((b) => b.slug === slug);
  const nextBook = books[(currentIdx + 1) % books.length];
  const relatedStory = book.story_slug ? getStoryBySlug(book.story_slug) : null;

  return (
    <article
      className="relative pb-24"
      style={{ background: `linear-gradient(180deg, ${book.color}22 0%, #F4FAFF 55%)` }}
      data-testid="book-page"
    >
      <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-32 md:pt-40">
        <Link
          to="/sklep"
          data-testid="book-back"
          className="inline-flex items-center gap-2 chip !bg-white/80 !border-white"
        >
          <ArrowLeft size={14} /> Wróć do sklepu
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-8 md:gap-14 items-start">
          {/* Cover */}
          <motion.div
            initial={{ opacity: 0, x: -20, rotate: -3 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-5 md:sticky md:top-32"
          >
            <div className="max-w-[380px] mx-auto md:mx-0">
              <BookCover book={book} size="lg" />
              <div className="mt-6 rounded-[1.5rem] bg-white border border-[#BAE6FD] p-5 shadow-[0_10px_40px_-15px_rgba(11,42,85,0.2)]">
                <div className="flex items-baseline justify-between">
                  <span className="font-display font-semibold text-3xl text-[#051937]" data-testid="book-price">
                    {book.price_pln} zł
                  </span>
                  <span className="text-xs uppercase tracking-[0.14em] text-[#0b2a55]/60 font-bold">
                    darmowa wysyłka od 100 zł
                  </span>
                </div>
                <motion.button
                  onClick={() => {
                    addItem(book.lookup_key);
                    toast.success(`${book.title} — do koszyka 📚`);
                  }}
                  whileHover={{ scale: 1.03, rotate: [-1.5, 1.5, -1.5, 0] }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.35 }}
                  className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white py-4 font-semibold shadow-[0_20px_50px_-20px_rgba(11,42,85,0.6)]"
                  data-testid="book-add-cart"
                >
                  <ShoppingBag size={18} /> Do koszyka
                </motion.button>
                <div className="mt-4 text-xs text-[#0b2a55]/70 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Check size={14} className="text-[#10B981]" />
                    Wysyłka w 2 dni robocze
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={14} className="text-[#10B981]" />
                    14 dni na zwrot bez podania przyczyny
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={14} className="text-[#10B981]" />
                    Pakowane ręcznie w Warszawie
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <div className="col-span-12 md:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="chip"
              style={{ background: `${book.color}88`, borderColor: book.accent }}
            >
              <span className="dot" /> {book.language === "EN" ? "English edition" : "Wydanie polskie"}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight"
              data-testid="book-title"
            >
              {book.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="mt-4 font-script text-2xl md:text-3xl text-[#60A5FA]"
            >
              {book.tagline}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.9 }}
              className="mt-8 text-lg text-[#0b2a55]/85 leading-relaxed"
            >
              {book.description}
            </motion.p>

            {/* Specs */}
            <dl className="mt-10 grid grid-cols-2 gap-4 md:gap-6 max-w-lg">
              {[
                ["Wiek", book.ageRange],
                ["Strony", `${book.pages}`],
                ["Format", book.format],
                ["Tematy", book.themes.join(" · ")],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-white border border-[#BAE6FD]/70 p-4">
                  <dt className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0b2a55]/60">
                    {label}
                  </dt>
                  <dd className="mt-1 font-display font-semibold text-[#051937]">{value}</dd>
                </div>
              ))}
            </dl>

            {/* Highlights */}
            <div className="mt-10">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/70">
                Co jest w środku
              </h3>
              <ul className="mt-4 space-y-3">
                {book.highlights.map((h, i) => (
                  <motion.li
                    key={h}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3 text-[#0b2a55]/90"
                  >
                    <span
                      className="mt-1 w-6 h-6 rounded-full grid place-items-center shrink-0"
                      style={{ background: `${book.accent}22`, color: book.accent }}
                    >
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span>{h}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Preview */}
            <div className="mt-10 rounded-[1.5rem] p-6" style={{ background: `${book.color}44`, border: `1px solid ${book.accent}33` }}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#051937]">
                <BookOpen size={14} /> Spis rozdziałów
              </div>
              <ol className="mt-4 space-y-1.5 text-[#051937]">
                {book.preview_chapters.map((c) => (
                  <li key={c} className="font-display font-medium">
                    · {c}
                  </li>
                ))}
              </ol>
              {relatedStory && (
                <Link
                  to={`/bajka/${relatedStory.slug}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#051937] animated-underline"
                  data-testid="book-read-preview"
                >
                  Poczytaj bajkę online <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Next book */}
        <Link
          to={`/ksiazka/${nextBook.slug}`}
          data-testid="book-next"
          className="mt-24 group rounded-[1.75rem] bg-white border border-[#BAE6FD]/70 p-6 md:p-8 flex items-center justify-between hover:-translate-y-1 transition-transform"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0b2a55]/60">
              Następna książka
            </div>
            <div className="mt-2 font-display font-semibold text-xl md:text-2xl text-[#051937]">
              {nextBook.title}
            </div>
          </div>
          <span className="w-12 h-12 rounded-full grid place-items-center bg-[#F4FAFF] group-hover:bg-[#FDE68A] transition-colors">
            <ArrowRight />
          </span>
        </Link>
      </div>
    </article>
  );
}
